/**
 * A platform map image with a pin at a position, drawn by the shared `GameMap`.
 *
 * `pixel` is in the image's native pixels; the pin is placed as a percentage of
 * the image, so it stays on the spot at any rendered width.
 */

import { cleanPlatformName, type MapMarker, type PlatformLocatedMap } from '@swtorbit/shared-socket-events';
import { GameMap } from '@swtorbit/shared-components';

interface MapPinProps {
  /** Panel caption, e.g. "Planet map". */
  label: string;
  map: PlatformLocatedMap | null;
  /** Shown when there is no map at all for the position. */
  emptyText: string;
}

export function MapPin({ label, map, emptyText }: MapPinProps) {
  const caption = (
    <p className="fs-xs text-slate m-0 mb-2xs text-uppercase font-semibold">
      {label}{map?.name ? ` · ${cleanPlatformName(map.name)}` : ''}
    </p>
  );

  if (!map || !map.image) {
    return (
      <div>
        {caption}
        <div className="game-map__frame p-sm text-center">
          <span className="fs-sm text-slate">{map ? 'This map has no artwork in the game files.' : emptyText}</span>
        </div>
      </div>
    );
  }

  const { url, width, height } = map.image;
  const markers: MapMarker[] = map.pixel
    ? [{ id: 'pin', kind: 'dot', tone: 'target', x: (map.pixel.x / width) * 100, y: (map.pixel.y / height) * 100 }]
    : [];

  return (
    <div>
      {caption}
      <GameMap imageUrl={url} width={width} height={height} name={cleanPlatformName(map.name) ?? label} markers={markers} />
    </div>
  );
}
